import { io, Socket } from 'socket.io-client';

// Extract base URL without API path for WebSocket connection
// e.g., http://127.0.0.1:8080/api/v1/ -> http://127.0.0.1:8080
const getSocketUrl = (): string => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  try {
    const url = new URL(apiUrl);
    return `${url.protocol}//${url.host}`;
  } catch {
    return 'http://localhost:8080';
  }
};

const SOCKET_URL = getSocketUrl();

/**
 * WebSocket Manager - Client-side Socket.IO connection management
 * Provides singleton connection instance with automatic reconnection
 */
class WebSocketManager {
  private socket: Socket | null = null;
  private token: string | null = null;

  /**
   * Initialize WebSocket connection with JWT token
   */
  connect(token: string): Socket {
    if (this.socket?.connected && this.token === token) {
      console.log('✓ WebSocket already connected');
      return this.socket;
    }

    // Disconnect existing socket if token changed
    if (this.socket && this.token !== token) {
      console.log('Token changed, reconnecting WebSocket...');
      this.disconnect();
    }

    this.token = token;

    this.socket = io(SOCKET_URL, {
      auth: {
        token
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      transports: ['websocket', 'polling']
    });

    this.setupEventHandlers();

    console.log('✓ WebSocket connection initialized');
    return this.socket;
  }

  /**
   * Setup connection event handlers
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('✓ WebSocket connected:', this.socket?.id);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error.message);
    });

    this.socket.on('error', (error) => {
      console.error('WebSocket error:', error);
    });

    // Heartbeat mechanism
    this.socket.on('pong', () => {
      // Server acknowledged ping
    });
  }

  /**
   * Disconnect WebSocket
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.token = null;
      console.log('✓ WebSocket disconnected');
    }
  }

  /**
   * Get current socket instance
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Subscribe to event
   */
  on(event: string, handler: (...args: any[]) => void): void {
    this.socket?.on(event, handler);
  }

  /**
   * Unsubscribe from event
   */
  off(event: string, handler?: (...args: any[]) => void): void {
    if (handler) {
      this.socket?.off(event, handler);
    } else {
      this.socket?.off(event);
    }
  }

  /**
   * Emit event to server
   */
  emit(event: string, data?: any): void {
    this.socket?.emit(event, data);
  }
}

// Export singleton instance
export const webSocketManager = new WebSocketManager();
